import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, CheckSquare, Tags, Palette, Building2, Briefcase, Clock, Calendar, UserPlus, Edit3 } from 'lucide-react';
import { supabaseAdmin } from '../../config/supabase';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { toast } from '../../components/ui/use-toast';

interface DashboardStats {
  // Total counts for each business type
  totalTraders: number;
  totalCrafties: number;
  totalHospitality: number;
  totalProfessionals: number;
  // Pending (unverified) counts
  pendingTraders: number;
  pendingCrafties: number;
  pendingHospitality: number;
  pendingProfessionals: number;
  // Profession/category counts
  totalProfessions: number;
  totalCraftProfessions: number;
  totalHospitalityCategories: number;
  totalProfessionalsCategories: number;
}

// Interface for recent activity items
interface RecentActivity {
  id: number;
  store_name: string;
  type: 'trader' | 'craftie' | 'hospitality' | 'professional';
  created_at: string;
  updated_at: string;
  last_sign_in?: string | null;
}

const Dashboard = () => {
  const { adminUser } = useAdminAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalTraders: 0,
    totalCrafties: 0,
    totalHospitality: 0,
    totalProfessionals: 0,
    pendingTraders: 0,
    pendingCrafties: 0,
    pendingHospitality: 0,
    pendingProfessionals: 0,
    totalProfessions: 0,
    totalCraftProfessions: 0,
    totalHospitalityCategories: 0,
    totalProfessionalsCategories: 0
  });

  const [loading, setLoading] = useState(true);
  
  // Recent activity states
  const [recentlyCreated, setRecentlyCreated] = useState<RecentActivity[]>([]);
  const [recentlyModified, setRecentlyModified] = useState<RecentActivity[]>([]);
  const [recentLogins, setRecentLogins] = useState<RecentActivity[]>([]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!adminUser) {
        setLoading(false);
        return;
      }

      try {
        // Fetch all stats in parallel for better performance using supabaseAdmin to bypass RLS
        const [
          tradersResult,
          craftiesResult,
          hospitalityResult,
          professionalsResult,
          professionsResult,
          craftProfResult,
          hospitalityCatResult,
          professionalsCatResult,
          // Recent activity queries - get recent records with timestamps
          recentTradersResult,
          recentCraftiesResult,
          recentHospitalityResult,
          recentProfessionalsResult
        ] = await Promise.all([
          // Business counts with verified status
          supabaseAdmin.from('traders').select('id, verified'),
          supabaseAdmin.from('crafties').select('id, verified'),
          supabaseAdmin.from('hospitality').select('id, verified'),
          supabaseAdmin.from('professionals').select('id, verified'),
          // Profession/category counts
          supabaseAdmin.from('professions').select('id', { count: 'exact' }),
          supabaseAdmin.from('craft_professions').select('id', { count: 'exact' }),
          supabaseAdmin.from('hospitality_categories').select('id', { count: 'exact' }),
          supabaseAdmin.from('professionals_categories').select('id', { count: 'exact' }),
          // Recent activity - fetch with timestamps and user_id for login info
          supabaseAdmin.from('traders').select('id, store_name, created_at, updated_at, user_id').order('created_at', { ascending: false }).limit(10),
          supabaseAdmin.from('crafties').select('id, store_name, created_at, updated_at, user_id').order('created_at', { ascending: false }).limit(10),
          supabaseAdmin.from('hospitality').select('id, store_name, created_at, updated_at, user_id').order('created_at', { ascending: false }).limit(10),
          supabaseAdmin.from('professionals').select('id, store_name, created_at, updated_at, user_id').order('created_at', { ascending: false }).limit(10)
        ]);

        // Check for errors and log them
        if (tradersResult.error) console.error('Traders error:', tradersResult.error);
        if (craftiesResult.error) console.error('Crafties error:', craftiesResult.error);
        if (hospitalityResult.error) console.error('Hospitality error:', hospitalityResult.error);
        if (professionalsResult.error) console.error('Professionals error:', professionalsResult.error);
        if (professionsResult.error) console.error('Professions error:', professionsResult.error);
        if (craftProfResult.error) console.error('Craft professions error:', craftProfResult.error);
        if (hospitalityCatResult.error) console.error('Hospitality categories error:', hospitalityCatResult.error);
        if (professionalsCatResult.error) console.error('Professionals categories error:', professionalsCatResult.error);

        // Calculate stats from the results
        const tradersData = tradersResult.data || [];
        const craftiesData = craftiesResult.data || [];
        const hospitalityData = hospitalityResult.data || [];
        const professionalsData = professionalsResult.data || [];

        setStats({
          totalTraders: tradersData.length,
          totalCrafties: craftiesData.length,
          totalHospitality: hospitalityData.length,
          totalProfessionals: professionalsData.length,
          pendingTraders: tradersData.filter((t: { verified: boolean }) => !t.verified).length,
          pendingCrafties: craftiesData.filter((c: { verified: boolean }) => !c.verified).length,
          pendingHospitality: hospitalityData.filter((h: { verified: boolean }) => !h.verified).length,
          pendingProfessionals: professionalsData.filter((p: { verified: boolean }) => !p.verified).length,
          totalProfessions: professionsResult.count || 0,
          totalCraftProfessions: craftProfResult.count || 0,
          totalHospitalityCategories: hospitalityCatResult.count || 0,
          totalProfessionalsCategories: professionalsCatResult.count || 0
        });

        // Process recent activity data
        // Combine all recent records and tag them with their type
        const allRecent: RecentActivity[] = [
          ...(recentTradersResult.data || []).map((r: any) => ({ ...r, type: 'trader' as const })),
          ...(recentCraftiesResult.data || []).map((r: any) => ({ ...r, type: 'craftie' as const })),
          ...(recentHospitalityResult.data || []).map((r: any) => ({ ...r, type: 'hospitality' as const })),
          ...(recentProfessionalsResult.data || []).map((r: any) => ({ ...r, type: 'professional' as const }))
        ];

        // Sort by created_at for recently created (newest first)
        const sortedByCreated = [...allRecent].sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ).slice(0, 5);
        setRecentlyCreated(sortedByCreated);

        // Sort by updated_at for recently modified (newest first)
        // Only include items where updated_at differs from created_at (actually modified)
        const sortedByModified = [...allRecent]
          .filter(r => r.updated_at !== r.created_at)
          .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
          .slice(0, 5);
        setRecentlyModified(sortedByModified);

        // For recent logins, we need to fetch from auth.users
        // Get unique user_ids from all records
        const userIds = allRecent
          .map((r: any) => r.user_id)
          .filter((id: string | null) => id !== null);
        
        if (userIds.length > 0) {
          // Fetch user login data from auth
          const { data: authUsers } = await supabaseAdmin.auth.admin.listUsers();
          
          if (authUsers?.users) {
            // Create a map of user_id to last_sign_in_at
            const loginMap = new Map<string, string>();
            authUsers.users.forEach(user => {
              if (user.last_sign_in_at) {
                loginMap.set(user.id, user.last_sign_in_at);
              }
            });

            // Add last_sign_in to records and sort by it
            const withLogins = allRecent
              .map((r: any) => ({
                ...r,
                last_sign_in: r.user_id ? loginMap.get(r.user_id) : null
              }))
              .filter(r => r.last_sign_in)
              .sort((a, b) => new Date(b.last_sign_in!).getTime() - new Date(a.last_sign_in!).getTime())
              .slice(0, 5);
            
            setRecentLogins(withLogins);
          }
        }
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        toast({
          title: 'Error',
          description: 'Failed to load dashboard statistics. Please check your permissions.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [adminUser]);

  // Group stat cards by category for better organization
  const statCards = [
    // Business totals section
    {
      title: 'Total Traders',
      value: stats.totalTraders,
      icon: Users,
      color: 'bg-blue-500',
      route: '/admin/traders',
    },
    {
      title: 'Total Crafties',
      value: stats.totalCrafties,
      icon: Palette,
      color: 'bg-purple-500',
      route: '/admin/crafties',
    },
    {
      title: 'Total Hospitality',
      value: stats.totalHospitality,
      icon: Building2,
      color: 'bg-rose-500',
      route: '/admin/hospitality',
    },
    {
      title: 'Total Professionals',
      value: stats.totalProfessionals,
      icon: Briefcase,
      color: 'bg-indigo-500',
      route: '/admin/professionals',
    },
    // Pending approvals section
    {
      title: 'Pending Traders',
      value: stats.pendingTraders,
      icon: CheckSquare,
      color: 'bg-yellow-500',
      route: '/admin/traders?status=pending',
    },
    {
      title: 'Pending Crafties',
      value: stats.pendingCrafties,
      icon: CheckSquare,
      color: 'bg-orange-500',
      route: '/admin/crafties?status=pending',
    },
    {
      title: 'Pending Hospitality',
      value: stats.pendingHospitality,
      icon: CheckSquare,
      color: 'bg-amber-500',
      route: '/admin/hospitality?status=pending',
    },
    {
      title: 'Pending Professionals',
      value: stats.pendingProfessionals,
      icon: CheckSquare,
      color: 'bg-lime-500',
      route: '/admin/professionals?status=pending',
    },
    // Categories/Professions section
    {
      title: 'Service Professions',
      value: stats.totalProfessions,
      icon: Tags,
      color: 'bg-green-500',
      route: '/admin/categories?table=professions',
    },
    {
      title: 'Craft Professions',
      value: stats.totalCraftProfessions,
      icon: Tags,
      color: 'bg-teal-500',
      route: '/admin/categories?table=craft_professions',
    },
    {
      title: 'Hospitality Categories',
      value: stats.totalHospitalityCategories,
      icon: Tags,
      color: 'bg-cyan-500',
      route: '/admin/categories?table=hospitality_categories',
    },
    {
      title: 'Professional Categories',
      value: stats.totalProfessionalsCategories,
      icon: Tags,
      color: 'bg-emerald-500',
      route: '/admin/categories?table=professionals_categories',
    },
  ];

  // Helper function to format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Helper function to get type badge color
  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'trader': return 'bg-blue-100 text-blue-700';
      case 'craftie': return 'bg-purple-100 text-purple-700';
      case 'hospitality': return 'bg-rose-100 text-rose-700';
      case 'professional': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Helper function to get route for business type
  const getBusinessRoute = (type: string) => {
    switch (type) {
      case 'trader': return `/admin/traders`;
      case 'craftie': return `/admin/crafties`;
      case 'hospitality': return `/admin/hospitality`;
      case 'professional': return `/admin/professionals`;
      default: return '/admin';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Dashboard</h1>
      
      {/* Grid layout: 4 columns on xl screens for better organization of 12 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {statCards.map((card, index) => (
          <Link 
            key={index} 
            to={card.route} 
            className="block hover:scale-105 transition-transform duration-200"
          >
            <div className={`p-5 rounded-lg shadow-sm hover:shadow-md bg-white border-l-4 ${card.color.replace('bg-', 'border-')}`}>
              <div className="flex items-center">
                <div className={`p-3 rounded-full ${card.color} bg-opacity-20 mr-4`}>
                  <card.icon className={`w-5 h-5 ${card.color.replace('bg-', 'text-')}`} />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-600">{card.title}</h3>
                  <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Recent Activity Section */}
      <h2 className="text-xl font-bold text-gray-900 mt-10 mb-6">Recent Activity</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Recently Created Profiles */}
        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex items-center mb-4">
            <div className="p-2 rounded-full bg-green-100 mr-3">
              <UserPlus className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-800">Recently Created</h3>
          </div>
          <div className="space-y-3">
            {recentlyCreated.length > 0 ? (
              recentlyCreated.map((item) => (
                <Link 
                  key={`${item.type}-${item.id}`} 
                  to={getBusinessRoute(item.type)}
                  className="block p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.store_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeBadgeColor(item.type)}`}>
                          {item.type}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatRelativeTime(item.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No recent profiles</p>
            )}
          </div>
        </div>

        {/* Recently Modified Profiles */}
        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex items-center mb-4">
            <div className="p-2 rounded-full bg-amber-100 mr-3">
              <Edit3 className="w-5 h-5 text-amber-600" />
            </div>
            <h3 className="font-semibold text-gray-800">Recently Modified</h3>
          </div>
          <div className="space-y-3">
            {recentlyModified.length > 0 ? (
              recentlyModified.map((item) => (
                <Link 
                  key={`${item.type}-${item.id}`} 
                  to={getBusinessRoute(item.type)}
                  className="block p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.store_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeBadgeColor(item.type)}`}>
                          {item.type}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center">
                          <Edit3 className="w-3 h-3 mr-1" />
                          {formatRelativeTime(item.updated_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No recent modifications</p>
            )}
          </div>
        </div>

        {/* Recent Logins */}
        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex items-center mb-4">
            <div className="p-2 rounded-full bg-blue-100 mr-3">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-800">Recent Logins</h3>
          </div>
          <div className="space-y-3">
            {recentLogins.length > 0 ? (
              recentLogins.map((item) => (
                <Link 
                  key={`${item.type}-${item.id}`} 
                  to={getBusinessRoute(item.type)}
                  className="block p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.store_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeBadgeColor(item.type)}`}>
                          {item.type}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {item.last_sign_in ? formatRelativeTime(item.last_sign_in) : 'Never'}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No recent logins</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
